import React, { ReactNode, useMemo } from 'react';
import { CrmDataContext, useCrmData } from './CrmDataContext';
import { ChatProvider } from './ChatContext';
import { useCrmOrgData } from './hooks/useCrmOrgData';

import { LeadsProvider, useLeads } from './leads/LeadsContext';
import { TasksProvider, useTasks } from './tasks/TasksContext';
import { UsersProvider, useUsers } from './users/UsersContext';
import { AssignmentProvider } from './assignment/AssignmentContext';
import { TagsProvider } from '../features/settings/tags/context/TagsContext';
import { ContactsProvider, useContacts } from '../features/kanban/hooks/useContacts';
import { ProductsProvider } from '../features/products/hooks/useProducts';
import { OriginsProvider } from '../features/settings/origins/hooks/useOrigins';
import { QuickRepliesProvider } from '../features/settings/quick-replies/hooks/useQuickReplies';
import { WebhooksProvider } from '../features/settings/webhooks/hooks/useWebhooks';
import { ApiEndpointsProvider } from '../features/settings/webhooks/hooks/useApiEndpoints';
import { LossReasonsProvider } from '../features/settings/loss-reasons/hooks/useLossReasons';
import { useOrganization } from './OrganizationContext';

export { useCrmData };

const CrmDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();

    const {
        leads,
        funnelStages,
        funnels,
        selectedFunnelId,
        isLoading: isLoadingLeads,
        updateLeadStage,
        saveLead,
        deleteLead,
        setSelectedFunnelId,
        refreshLeads,
    } = useLeads();

    const { isLoading: isLoadingTasks } = useTasks();

    const {
        crmUsers,
        isLoading: isLoadingUsers,
    } = useUsers();

    const {
        contacts,
        loading: isLoadingContacts,
        refreshContacts,
        updateContact,
        createContact,
        deleteContact,
    } = useContacts();

    const {
        subscriptionPlans,
        clientProfile,
        whatsappConnections,
        metaAccounts,
        subscription,
        isLoading: isLoadingOrgData,
        saveClientProfile,
        updateSubscription,
    } = useCrmOrgData(currentOrganization, crmUsers.length);

    const isLoading =
        isLoadingLeads || isLoadingTasks || isLoadingUsers || isLoadingContacts || isLoadingOrgData;

    const value = useMemo(
        () => ({
            leads,
            contacts,
            crmUsers,
            funnelStages,
            funnels,
            selectedFunnelId,
            subscriptionPlans,
            clientProfile,
            whatsappConnections,
            metaAccounts,
            subscription,
            isLoading,
            updateLeadStage,
            saveLead,
            deleteLead,
            setSelectedFunnelId,
            refreshLeads,
            saveClientProfile,
            updateSubscription,
            refreshContacts,
            updateContact,
            createContact,
            deleteContact,
        }),
        [
            leads,
            contacts,
            crmUsers,
            funnelStages,
            funnels,
            selectedFunnelId,
            subscriptionPlans,
            clientProfile,
            whatsappConnections,
            metaAccounts,
            subscription,
            isLoading,
            updateLeadStage,
            saveLead,
            deleteLead,
            setSelectedFunnelId,
            refreshLeads,
            saveClientProfile,
            updateSubscription,
            refreshContacts,
            updateContact,
            createContact,
            deleteContact,
        ]
    );

    return (
        <CrmDataContext.Provider value={value}>
            {children}
        </CrmDataContext.Provider>
    );
};

export const CrmProviders: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <UsersProvider>
            <ContactsProvider>
                <ProductsProvider>
                    <OriginsProvider>
                        <QuickRepliesProvider>
                            <LossReasonsProvider>
                                <WebhooksProvider>
                                    <ApiEndpointsProvider>
                                        <TagsProvider>
                                            <LeadsProvider>
                                                <TasksProvider>
                                                    <AssignmentProvider>
                                                        <ChatProvider>
                                                            <CrmDataProvider>
                                                                {children}
                                                            </CrmDataProvider>
                                                        </ChatProvider>
                                                    </AssignmentProvider>
                                                </TasksProvider>
                                            </LeadsProvider>
                                        </TagsProvider>
                                    </ApiEndpointsProvider>
                                </WebhooksProvider>
                            </LossReasonsProvider>
                        </QuickRepliesProvider>
                    </OriginsProvider>
                </ProductsProvider>
            </ContactsProvider>
        </UsersProvider>
    );
};
